import { api } from './api';
import { ResponseType } from '../types/shared.types';
import {
  ApplyMenuPriceFixDto,
  ChatTriagePilotLocation,
  GetOrderCandidatesParams,
  GetSupportTicketsParams,
  MenuPriceFixApplied,
  MenuPriceFixesResponse,
  OrderCandidateRow,
  ReplySupportTicketDto,
  ResolveSupportTicketDto,
  SupportTicket,
  SupportTicketListResponse,
  SupportTicketOpenCountResponse,
} from '../types/supportTicket.types';

/**
 * Support tickets - the requests the AI triage bot hands to the team, and the
 * pilot switch that decides which merchants it listens to at all.
 *
 * Admin app only: every route wants `conversationRead` or `conversationWrite`,
 * which is the same permission the support inbox is delegated with. See
 * `types/supportTicket.types` for the domain.
 */

export const SUPPORT_TICKETS_PAGE_SIZE = 20;

/**
 * Sorted server side and not negotiable: open before claimed before resolved,
 * and OLDEST first inside each - the ticket that has waited longest is the one
 * at the top.
 */
export const getSupportTickets = (params: GetSupportTicketsParams) =>
  api.get<ResponseType<SupportTicketListResponse>>('/v1/support-tickets', {
    params,
  });

/** The sidebar badge's starting number. After that the socket carries it. */
export const getSupportTicketOpenCount = () =>
  api.get<ResponseType<SupportTicketOpenCountResponse>>(
    '/v1/support-tickets/open-count'
  );

export const getSupportTicket = (ticketId: number | string) =>
  api.get<ResponseType<SupportTicket>>(`/v1/support-tickets/${ticketId}`);

/** First come, first served: a second taker gets a 400 naming the first. */
export const claimSupportTicket = (ticketId: number) =>
  api.post<ResponseType<SupportTicket>>(
    `/v1/support-tickets/${ticketId}/claim`
  );

/** Back into the open pile. Only a claimed ticket can be handed back. */
export const releaseSupportTicket = (ticketId: number) =>
  api.post<ResponseType<SupportTicket>>(
    `/v1/support-tickets/${ticketId}/release`
  );

/**
 * Close it, and write the resolution into the merchant's thread as a reply to
 * the message that started it - so the text is addressed to them, not kept as
 * an internal note. Only the person who claimed the ticket may do this.
 */
export const resolveSupportTicket = (
  ticketId: number,
  data: ResolveSupportTicketDto
) =>
  api.post<ResponseType<SupportTicket>>(
    `/v1/support-tickets/${ticketId}/resolve`,
    data
  );

/**
 * Write to the merchant WITHOUT closing the ticket: a question, an update.
 * Lands in their thread the same way a resolution does, as a reply to the
 * message that opened the ticket.
 *
 * Works on an open ticket too, which it claims for the caller first (the
 * answer comes back `claimed`). Somebody else's claim, or a resolved ticket,
 * is a 400 whose message says which.
 */
export const replySupportTicket = (
  ticketId: number,
  data: ReplySupportTicketDto
) =>
  api.post<ResponseType<SupportTicket>>(
    `/v1/support-tickets/${ticketId}/reply`,
    data
  );

/**
 * The orders this ticket could be about: the merchant's own, across all their
 * locations, last 30 days, newest first. The picker's whole source - the
 * general orders list is deliberately not used, since it is not scoped to one
 * merchant and would offer somebody else's order.
 */
export const getSupportTicketOrderCandidates = (
  ticketId: number,
  params: GetOrderCandidatesParams
) =>
  api.get<ResponseType<OrderCandidateRow[]>>(
    `/v1/support-tickets/${ticketId}/order-candidates`,
    { params }
  );

/**
 * Attach an order the merchant did not pick themselves. The order has to
 * belong to the ticket's merchant; the 30 day window is the list's, not this
 * route's, so an older order of theirs still attaches by id.
 */
export const attachSupportTicketOrder = (ticketId: number, orderId: number) =>
  api.post<ResponseType<SupportTicket>>(
    `/v1/support-tickets/${ticketId}/order/${orderId}`
  );

/**
 * The menu prices the merchant says are wrong, each with the dishes it could
 * mean, and the ones already approved. Nothing on the ticket says whether
 * there are any, so the detail asks every time it opens. Prices in CENTS.
 */
export const getSupportTicketMenuPriceFixes = (ticketId: number) =>
  api.get<ResponseType<MenuPriceFixesResponse>>(
    `/v1/support-tickets/${ticketId}/menu-price-fixes`
  );

/**
 * Set one dish's price. Saved even when the publish to the platforms waits
 * (`published: false`). Claims an open ticket for the caller, and neither
 * resolves it nor writes to the merchant.
 */
export const applySupportTicketMenuPriceFix = (
  ticketId: number,
  data: ApplyMenuPriceFixDto
) =>
  api.post<ResponseType<MenuPriceFixApplied>>(
    `/v1/support-tickets/${ticketId}/menu-price-fixes/apply`,
    data
  );

/* -------------------------------------------------------------------------- */
/* Chat router pilot (role admin)                                             */
/* -------------------------------------------------------------------------- */

/** Only the ENABLED locations come back, so membership is the whole answer. */
export const getChatTriagePilotLocations = () =>
  api.get<ResponseType<ChatTriagePilotLocation[]>>(
    '/v1/chat-triage/pilot-locations'
  );

export const setChatTriagePilotLocation = (
  locationId: number,
  enabled: boolean
) =>
  api.put<ResponseType<ChatTriagePilotLocation>>(
    `/v1/chat-triage/pilot-locations/${locationId}`,
    { enabled }
  );
